import { useState, useEffect, useRef, useMemo } from 'react';
import { Map } from 'react-map-gl/maplibre';
import { AmbientLight, PointLight, LightingEffect } from '@deck.gl/core';
import { DeckGL } from '@deck.gl/react';
import { TextLayer, PathLayer } from '@deck.gl/layers';
import { SimpleMeshLayer } from '@deck.gl/mesh-layers';
import { Toaster, toast } from 'sonner';
import * as Sentry from '@sentry/react';


import type { MapViewState } from '@deck.gl/core';
import type { Vehicle, RoutePath, RouteInfo } from './types/gtfs-rt';

import { getShapesData, getRoutesData } from './services/gtfs-static';
import { OperatorSelector } from './components/OperatorSelector';
import { ApiKeySetup } from './components/ApiKeySetup';
import { StorageManager } from './components/StorageManager';
import { getVehicleMesh, getVehicleMeshScale } from './utils/vehicleMeshes';
import { getVehicleType } from './utils/vehicleTypes';
import { useVehicleTracker } from './hooks/useVehicleTracker';

// Helper to convert hex color to RGB array for deck.gl
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [255, 255, 255];
};
import {
  MAP_CONFIG,
  OPERATORS,
  getRealtimeApiKey,
  getSelectedOperator,
  setSelectedOperator,
  getPollInterval,
  setPollInterval
} from './config';

const ambientLight = new AmbientLight({
  color: [255, 255, 255],
  intensity: 1.0
});

const pointLight = new PointLight({
  color: [255, 255, 255],
  intensity: 2.0,
  position: [18.0686, 59.3293, 8000] // Stockholm
});

const lightingEffect = new LightingEffect({ ambientLight, pointLight });

const effects = [lightingEffect];

// Calculate zoom-dependent scale factor
// Inverted: large zoom (zoomed in) gives small scale (high detail)
const getZoomScaleFactor = (zoom: number): number => {
  // Map zoom levels to scale multiplier
  // Zoom 18+: 1x (small, detailed), Zoom 5: 20x (large, visible from far)
  const minZoom = 5;
  const maxZoom = 18;
  const minScale = 1;
  const maxScale = 20;

  const normalizedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));
  // Invert: high zoom → low scale, low zoom → high scale
  return maxScale - ((normalizedZoom - minZoom) / (maxZoom - minZoom)) * (maxScale - minScale);
};

export default function App() {
  const [realtimeApiKey, setRealtimeApiKeyState] = useState<string>(getRealtimeApiKey());
  const [showSetup, setShowSetup] = useState<boolean>(!getRealtimeApiKey());
  const [showStorage, setShowStorage] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperatorState] = useState(getSelectedOperator());
  const [viewState, setViewState] = useState<MapViewState>(MAP_CONFIG.INITIAL_VIEW_STATE);
  const viewStateRef = useRef<MapViewState>(viewState);

  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  const [pollInterval, setPollIntervalState] = useState<number>(getPollInterval());
  const [showRoutes, setShowRoutes] = useState<boolean>(false);
  const [routePaths, setRoutePaths] = useState<RoutePath[]>([]);
  const [availableRouteTypes, setAvailableRouteTypes] = useState<Set<string | number>>(new Set());
  const [visibleRouteTypes, setVisibleRouteTypes] = useState<Set<string | number>>(new Set());
  const [showLabels, setShowLabels] = useState<boolean>(true);
  const deckRef = useRef<any>(null);
  const [hoveredObjects, setHoveredObjects] = useState<any[]>([]);

  // Use custom hook for vehicle tracking
  const { vehicles, isLoading, lastUpdate } = useVehicleTracker({
    selectedOperator,
    realtimeApiKey,
    pollInterval,
    isPaused: showSetup
  });

  // Handle API key setup completion
  const handleSetupComplete = (newRealtimeApiKey: string, operator: string, interval: number) => {
    setRealtimeApiKeyState(newRealtimeApiKey);
    setSelectedOperatorState(operator);
    setPollIntervalState(interval);
    setShowSetup(false);
    toast.success('Configuration saved! Starting vehicle tracking...', {
      duration: 2000
    });
  };

  // Handle poll interval change
  const handlePollIntervalChange = (interval: number) => {
    setPollIntervalState(interval);
    setPollInterval(interval);
    toast.success(`Update interval changed to ${interval / 1000}s`, {
      duration: 2000
    });
  };

  // Handle settings click
  const handleSettingsClick = () => {
    setShowSetup(true);
  };

  // Handle route type toggle
  const handleRouteTypeToggle = (type: string | number) => {
    setVisibleRouteTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  // Load static route types when operator or keys change
  useEffect(() => {
    if (showSetup) return;

    const loadRouteTypes = async () => {
      try {
        console.log('[Route Types] Loading for operator:', selectedOperator);
        const routesData = await getRoutesData(selectedOperator);

        // Extract unique types from routes data
        const types = new Set(
          Object.values(routesData.routes).map((route: RouteInfo) => route.type)
        );

        console.log('[Route Types] Available types:', Array.from(types));
        setAvailableRouteTypes(types);

        // Auto-enable all types on first load
        if (visibleRouteTypes.size === 0) {
          setVisibleRouteTypes(types);
        }
      } catch (error) {
        console.error('[Route Types] Failed to load:', error);
        Sentry.captureException(error, {
          tags: {
            feature: 'load-route-types',
            operator: selectedOperator
          }
        });
        if (error instanceof Error && error.message.includes('quota')) {
          toast.error('Storage quota exceeded. Please clear cache.', {
            action: {
              label: 'Manage Storage',
              onClick: () => setShowStorage(true)
            },
            duration: 10000
          });
        }
      }
    };

    loadRouteTypes();
  }, [selectedOperator, showSetup]);

  // Update view when operator changes
  const handleOperatorChange = (operatorId: string) => {
    // Clear route paths and types so they get reloaded for new operator
    setRoutePaths([]);
    setAvailableRouteTypes(new Set());
    setVisibleRouteTypes(new Set());

    // Save operator selection
    setSelectedOperator(operatorId);
    setSelectedOperatorState(operatorId);

    const operator = OPERATORS[operatorId];
    if (operator) {
      setViewState({
        ...viewState,
        longitude: operator.center[0],
        latitude: operator.center[1],
        zoom: operator.zoom,
        transitionDuration: 1000
      });
    }
  };

  // Load route shapes when showRoutes is toggled
  useEffect(() => {
    if (!showRoutes) {
      return;
    }

    // Only load if we don't already have data
    if (routePaths.length > 0) {
      console.log('[Load Shapes] Already have data, skipping load');
      return;
    }

    const loadShapes = async () => {
      try {
        console.log('[Load Shapes] Starting to load...');
        toast.loading('Loading route paths...', { id: 'load-shapes' });

        // Fetch shapes and routes data from Trafiklab (or cache)
        const [shapesData, routesData] = await Promise.all([
          getShapesData(selectedOperator),
          getRoutesData(selectedOperator)
        ]);

        console.log('[Load Shapes] Data fetched, processing routes...');

        const paths: RoutePath[] = [];

        // For each route, take only the first shape variant to reduce memory
        Object.entries(shapesData.routes).forEach(([routeId, routeData]: [string, any]) => {
          const shapeIds = Object.keys(routeData.shapes);
          if (shapeIds.length === 0) return;

          // Use the first shape (could be improved to pick the "main" variant)
          const shapeId = shapeIds[0];
          const shape = routeData.shapes[shapeId];

          // Get route color from cached routes data
          const colorHex = routesData?.routes[routeId]?.color || '#8C8C8C';
          
          // Convert hex to RGB
          const hex = colorHex.replace('#', '');
          const rgb: [number, number, number] = [
            parseInt(hex.substring(0, 2), 16),
            parseInt(hex.substring(2, 4), 16),
            parseInt(hex.substring(4, 6), 16)
          ];

          paths.push({
            routeId,
            shapeId,
            shortName: routeData.shortName,
            type: routeData.type,
            coordinates: shape.coordinates as Array<[number, number]>,
            color: rgb
          });
        });

        console.log(`[Load Shapes] Processed ${paths.length} paths, setting state...`);
        console.log(`[Load Shapes] Sample path:`, paths[0]);
        setRoutePaths(paths);
        console.log(`[Load Shapes] State updated!`);

        toast.success(`Loaded ${paths.length} route paths`, {
          id: 'load-shapes',
          duration: 2000
        });
        console.log(`Loaded ${paths.length} route paths with ${paths.reduce((sum, p) => sum + p.coordinates.length, 0).toLocaleString()} total points`);
      } catch (error) {
        console.error('Failed to load shapes:', error);
        Sentry.captureException(error, {
          tags: {
            feature: 'load-shapes',
            operator: selectedOperator
          }
        });

        if (error instanceof Error && error.message.includes('quota')) {
          toast.error('Storage quota exceeded. Please clear cache.', {
            id: 'load-shapes',
            action: {
              label: 'Manage Storage',
              onClick: () => setShowStorage(true)
            },
            duration: 10000
          });
        } else {
          toast.error('Failed to load route paths', {
            id: 'load-shapes',
            duration: 3000
          });
        }
        setShowRoutes(false); // Turn off if loading failed
      }
    };

    loadShapes();
  }, [showRoutes, routePaths.length, selectedOperator]);

  // Group vehicles by type for mesh layers
  const vehiclesByType = useMemo(() => {
    return vehicles.reduce((acc, vehicle) => {
      // Only include vehicles whose types are visible
      if (!visibleRouteTypes.has(vehicle.vehicleType)) {
        return acc;
      }
      
      const type = vehicle.vehicleType;
      if (!acc[type]) acc[type] = [];
      acc[type].push(vehicle);
      return acc;
    }, {} as Record<string, Vehicle[]>);
  }, [vehicles, visibleRouteTypes]);

  // Create vehicle layers - one SimpleMeshLayer per vehicle type
  const meshLayers = Object.entries(vehiclesByType).map(([type, vehiclesOfType]) => {
    const baseScale = getVehicleMeshScale(type);

    return new SimpleMeshLayer<Vehicle>({
      id: `vehicle-meshes-${type}`,
      data: vehiclesOfType,
      pickable: true,
      mesh: getVehicleMesh(type),
      getPosition: (d: Vehicle) => d.position,
      getColor: (d: Vehicle) => [...hexToRgb(d.color), 255],
      getOrientation: (d: Vehicle) => [0, -d.bearing, 0],
      getScale: () => {
        const zoom = viewStateRef.current?.zoom ?? MAP_CONFIG.INITIAL_VIEW_STATE.zoom;
        const zoomScaleFactor = getZoomScaleFactor(zoom);
        const scale = baseScale * zoomScaleFactor;
        return [scale, scale, scale];
      },
      wireframe: false,
      material: {
        ambient: 0.35,
        diffuse: 0.6,
        shininess: 32,
        specularColor: [255, 255, 255]
      },
      updateTriggers: {
        getPosition: vehiclesOfType.map(v => `${v.position[0]},${v.position[1]}`).join(','),
        getColor: vehiclesOfType.map(v => v.color).join('|'),
        getOrientation: vehiclesOfType.map(v => v.bearing).join(','),
        // Use the reactive state value here (primitive) so deck.gl can detect changes.
        getScale: viewState.zoom
      }
    });
  });

  // Create stable string key for visible types to use in updateTriggers
  const visibleTypesKey = useMemo(() => {
    return Array.from(visibleRouteTypes).sort().join(',');
  }, [visibleRouteTypes]);

  // Filter route paths by visible types (memoized for stability)
  const filteredRoutePaths = useMemo(() => {
    return routePaths.filter(route => visibleRouteTypes.has(route.type));
  }, [routePaths, visibleRouteTypes]);

  // Debug logging for path rendering
  useEffect(() => {
    if (showRoutes) {
      console.log(`[Routes Debug] showRoutes=${showRoutes}, routePaths=${routePaths.length}, visibleRouteTypes=${Array.from(visibleRouteTypes).join(',')}`, 
        `filtered=${filteredRoutePaths.length}`);
    }
  }, [showRoutes, routePaths, visibleRouteTypes, filteredRoutePaths]);

  const layers = [
    // Route paths layer (render first so vehicles appear on top)
    new PathLayer<RoutePath>({
      id: 'route-paths',
      data: filteredRoutePaths,
      visible: showRoutes && filteredRoutePaths.length > 0,
      pickable: true,
      widthScale: 20,
      widthMinPixels: 1,
      widthMaxPixels: 5,
      getPolygonOffset: (params: { layerIndex: number }) => [-params.layerIndex * 1000, 0],
      getPath: (d: RoutePath) => d.coordinates,
      getColor: (d: RoutePath) => {
        const color = [...d.color, 255] as [number, number, number, number];
        return color;
      },
      getWidth: () => 6,
      opacity: 1,
      rounded: true,      
      capRounded: true,
      jointRounded: true,
      billboard: false,
      updateTriggers: {
        // Use stable primitive values for update detection
        getPath: visibleTypesKey,
        getColor: visibleTypesKey,
        getWidth: visibleTypesKey
      },
      onDataLoad: () => {
        console.log(`[PathLayer] Loaded ${filteredRoutePaths.length} paths, visible=${showRoutes}`);
        if (filteredRoutePaths.length > 0) {
          console.log(`[PathLayer] First path:`, filteredRoutePaths[0]);
        }
      },
    }),

    ...meshLayers,

    // Route number labels layer
    new TextLayer<Vehicle>({
      id: 'route-labels',
      data: vehicles.filter(v => visibleRouteTypes.has(v.vehicleType)),
      visible: showLabels,
      pickable: false,
      getPosition: (d: Vehicle) => d.position,
      getText: (d: Vehicle) => d.routeShortName,
      getSize: 12,
      getColor: [255, 255, 255, 255],
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'bottom',
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      outlineWidth: 2,
      outlineColor: [0, 0, 0, 255],
      getPixelOffset: [0, -20], // Above the 3D model
      billboard: true,
      sizeScale: 1,
      updateTriggers: {
        getPosition: vehicles.map(v => `${v.position[0]},${v.position[1]}`).join(','),
        getText: vehicles.map(v => v.routeShortName).join(','),
        data: Array.from(visibleRouteTypes).join(',')
      }
    })
  ];

  return (
    <>
      <Toaster
        position="top-right"
        theme="dark"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid #333'
          }
        }}
      />

      {showSetup && (
        <ApiKeySetup onComplete={handleSetupComplete} />
      )}

      <StorageManager
        isOpen={showStorage}
        onClose={() => setShowStorage(false)}
      />

      {!showSetup && (
        <>
          <DeckGL
            ref={deckRef}
            layers={layers}
            effects={effects}
            initialViewState={viewState}
            viewState={viewState}
            onViewStateChange={({ viewState }) => {
              setViewState(viewState as MapViewState);
            }}
            controller={true}
            onHover={(info) => {
              if (!info.x || !info.y) {
                setHoveredObjects([]);
                return;
              }

              // Pick multiple objects at this position
              if (deckRef.current) {
                const pickInfos = deckRef.current.pickMultipleObjects({
                  x: info.x,
                  y: info.y,
                  radius: 1,
                  depth: 10 // Get up to 10 overlapping objects
                });
                setHoveredObjects(pickInfos.map((p: any) => p.object).filter(Boolean));
              }
            }}
            getTooltip={() => {
              if (hoveredObjects.length === 0) return null;

              // Separate vehicles and routes
              const vehicles = hoveredObjects.filter(obj => 'speed' in obj) as Vehicle[];
              const routes = hoveredObjects.filter(obj => !('speed' in obj)) as RoutePath[];

              // Build combined HTML
              let html = '<div style="font-family: monospace; font-size: 12px;">';

              // Show vehicles first
              if (vehicles.length > 0) {
                vehicles.forEach((vehicle, idx) => {
                  html += `
                    <div style="${idx > 0 ? 'margin-top: 12px; padding-top: 8px; border-top: 1px solid #444;' : ''}">
                      <div style="font-weight: bold; color: ${vehicle.color}; margin-bottom: 4px;">
                        🚌 ${vehicle.routeShortName} ${vehicle.routeLongName ? `- ${vehicle.routeLongName}` : ''}
                      </div>
                      <div>Type: ${vehicle.vehicleType}</div>
                      <div>Speed: ${(vehicle.speed * 3.6).toFixed(1)} km/h</div>
                      <div>Bearing: ${vehicle.bearing.toFixed(0)}°</div>
                      <div style="margin-top: 4px; color: #888; font-size: 10px;">
                        ${new Date(vehicle.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  `;
                });
              }

              // Show routes
              if (routes.length > 0) {
                if (vehicles.length > 0) {
                  html += '<div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #444;"></div>';
                }
                html += `<div style="font-weight: bold; margin-bottom: 4px;">Routes at this location (${routes.length}):</div>`;
                routes.forEach(route => {
                  const vehicleType = getVehicleType(route.type);
                  html += `
                    <div style="margin-left: 8px; margin-top: 4px;">
                      <span style="color: ${route.color}; font-weight: bold;">●</span>
                      <span style="margin-left: 4px;">${route.shortName} (${vehicleType.displayName})</span>
                    </div>
                  `;
                });
              }

              html += '</div>';

              return {
                html,
                style: {
                  backgroundColor: 'rgba(0, 0, 0, 0.95)',
                  color: '#fff',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '2px solid #555',
                  maxWidth: '300px'
                }
              };
            }}
          >
            <Map
              reuseMaps
              mapStyle={MAP_CONFIG.STYLE}
            />
          </DeckGL>

          <OperatorSelector
            selectedOperator={selectedOperator}
            onOperatorChange={handleOperatorChange}
            vehicleCount={vehicles.length}
            lastUpdate={lastUpdate}
            isLoading={isLoading}
            pollInterval={pollInterval}
            onPollIntervalChange={handlePollIntervalChange}
            onSettingsClick={handleSettingsClick}
            showRoutes={showRoutes}
            onShowRoutesChange={setShowRoutes}
            availableRouteTypes={availableRouteTypes}
            visibleRouteTypes={visibleRouteTypes}
            onRouteTypeToggle={handleRouteTypeToggle}
            showLabels={showLabels}
            onShowLabelsChange={setShowLabels}
          />

          {/* Add a small button to access storage manager directly if needed, or rely on toast action */}
          <button
            onClick={() => setShowStorage(true)}
            style={{
              position: 'absolute',
              bottom: '16px',
              right: '16px',
              padding: '8px',
              background: '#1a1a1a',
              color: '#9ca3af', // text-gray-400
              borderRadius: '4px',
              border: '1px solid #333',
              fontSize: '12px',
              zIndex: 1001, // Ensure it's above OperatorSelector (1000)
              cursor: 'pointer'
            }}
            title="Storage Management"
          >
            💾
          </button>
        </>
      )}
    </>
  );
}
